import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { FleetRescoreAction } from '@/components/admin/organizations/fleet-rescore-action';
import { OrganizationsTable } from '@/components/admin/organizations/organizations-table';
import { OrgCreate } from '@/components/admin/organizations/org-create/org-create';
import { OrganizationPicker } from '@/components/admin/shared/organization-picker';
import { BulkDeleteBar } from '@/components/admin/shared/bulk-delete-bar';
import { useBulkSelection } from '@/components/admin/shared/use-bulk-selection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAdminOrganizations,
  useBulkDeleteOrganizations,
} from '@/hooks/data/admin/use-organizations';
import { useDebounced } from '@/hooks/primitives/use-debounced';
import type { OrganizationKind, OrganizationStatus } from '@/types/generated';

const searchSchema = z.object({
  status: z.enum(['ACTIVE', 'SUSPENDED', 'DELETED']).optional(),
  kind: z.enum(['ADVERTISER', 'AGENCY']).optional(),
  managed_by: z.number().int().positive().optional(),
  q: z.string().optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/organizations/')({
  validateSearch: searchSchema,
  staticData: { title: 'Organizations' },
  component: AdminOrganizationsListPage,
});

function AdminOrganizationsListPage(): React.ReactElement {
  const { status, kind, managed_by, q, offset } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const bulkDelete = useBulkDeleteOrganizations();

  const [searchInput, setSearchInput] = useState(q ?? '');
  const debouncedSearch = useDebounced(searchInput, 300);

  const orgsQuery = useAdminOrganizations({
    status: status,
    kind,
    managed_by_organization_id: managed_by,
    search: debouncedSearch || undefined,
    limit: LIMIT,
    offset,
  });

  const bulk = useBulkSelection({
    rows: orgsQuery.data?.items ?? [],
    getId: (org) => org.id,
    getLabel: (org) => org.name,
    isSelectable: (org) => org.status !== 'DELETED',
  });

  const setStatus = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        status: value === 'ALL' ? undefined : (value as OrganizationStatus),
        offset: 0,
      }),
    });
  };

  const setKind = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        kind: value === 'ALL' ? undefined : (value as OrganizationKind),
        offset: 0,
      }),
    });
  };

  const onSearchChange = (value: string): void => {
    setSearchInput(value);
    void navigate({
      search: (s) => ({ ...s, q: value || undefined, offset: 0 }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="Organizations"
        description="Manage all tenant organizations"
        actions={
          <>
            <FleetRescoreAction basis="mixed_basis" />
            <FleetRescoreAction basis="counted_event" />
            <Button onClick={() => setCreateOpen(true)}>
              Create organization
            </Button>
          </>
        }
      />
      <OrgCreate open={createOpen} onOpenChange={setCreateOpen} />
      <FilterBar>
        <Select value={status ?? 'ALL'} onValueChange={setStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="SUSPENDED">Suspended</SelectItem>
            <SelectItem value="DELETED">Deleted</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kind ?? 'ALL'} onValueChange={setKind}>
          <SelectTrigger className="w-40" aria-label="Kind">
            <SelectValue placeholder="Kind" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All kinds</SelectItem>
            <SelectItem value="ADVERTISER">Advertisers</SelectItem>
            <SelectItem value="AGENCY">Agencies</SelectItem>
          </SelectContent>
        </Select>
        <OrganizationPicker
          value={managed_by}
          onChange={(value) =>
            void navigate({
              search: (s) => ({ ...s, managed_by: value, offset: 0 }),
            })
          }
          kind="AGENCY"
          placeholder="Any managing agency"
        />
        <Input
          placeholder="Search organizations…"
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64"
        />
      </FilterBar>
      <BulkDeleteBar
        items={bulk.items}
        onClear={bulk.clear}
        onSelectionChange={bulk.setSelectedIds}
        resource="organization"
        isPending={bulkDelete.isPending}
        onDelete={bulkDelete.mutateAsync}
      />
      <OrganizationsTable
        query={orgsQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
        selection={bulk.selection}
      />
    </main>
  );
}
